import type { LogLevel, LoggerOptions } from "../types";

const LEVEL_WEIGHT: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Creates a logger instance with configurable logging levels, formatting, and output options.
 *
 * @param options - Logger configuration options object
 * @param options.level - Minimum log level to output: 'debug', 'info', 'warn', or 'error' (default: "info")
 * @param options.enabled - Whether logging is enabled (default: true)
 * @param options.prefix - Optional prefix/scope to include in log messages
 * @param options.json - Whether to output logs as JSON format (default: false)
 * @returns A logger object with debug, info, warn, error, and json methods
 */
export function createLogger(options: LoggerOptions = {}) {
  const { level = "info", enabled = true, prefix, json = false } = options;

  function log(type: LogLevel, message: string, data?: unknown) {
    if (!enabled) return;
    if (LEVEL_WEIGHT[type] < LEVEL_WEIGHT[level]) return;

    const payload = {
      time: new Date().toISOString(),
      level: type,
      scope: prefix,
      message,
      data,
    };

    if (json) {
      console[type](JSON.stringify(payload));
    } else {
      const tag = prefix ? `[${prefix}]` : "";
      console[type](
        `[${payload.time}] ${type.toUpperCase()} ${tag}`,
        message,
        data ?? ""
      );
    }
  }

  return {
    debug: (message: string, data?: unknown) => log("debug", message, data),
    info: (message: string, data?: unknown) => log("info", message, data),
    warn: (message: string, data?: unknown) => log("warn", message, data),
    error: (message: string, data?: unknown) => log("error", message, data),
    json: (message: string, data?: unknown) => log("debug", JSON.stringify({ message, data }), data),
  };
}
